import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Button, Card, Spinner } from 'react-bootstrap';
import { FaFileExcel } from 'react-icons/fa';
import { toast } from 'react-toastify';
import moment from 'moment';
import HeaderAzul from '~/Components/HeaderAzul/HeaderAzul';
import * as api from '~/Services/Service';
import PainelFiltros from './components/PainelFiltros';
import TabelaPrincipal from './components/TabelaPrincipal';
import ChipsFiltros from './components/ChipsFiltros';

const PAGE_SIZE = 50;

const ESTADO_INICIAL_FILTROS = {
  codigoGrupoEmpresa: null,
  codigoGrupoGe: null,
  codigoFamilia: null,
  segmentos: ['E', 'A', 'T', 'P'],
  estagios: ['1', '2', '3', '4', '5', '6'],
};

const ORDENACAO_INICIAL = { ordenarPor: 'ge_nome', direcao: 'ASC' };

const FichaEmpreendimento = () => {
  const [filtrosAplicados, setFiltrosAplicados] = useState(null);
  const [resultado, setResultado] = useState(null);
  const [carregando, setCarregando] = useState(false);
  const [exportando, setExportando] = useState(false);
  const [ordenacao, setOrdenacao] = useState(ORDENACAO_INICIAL);
  const [paginaAtual, setPaginaAtual] = useState(1);

  const montarRequest = (filtros, page, orden) => ({
    codigo_grupo_empresa: filtros.codigoGrupoEmpresa || null,
    codigo_grupo_ge: filtros.codigoGrupoGe || null,
    codigo_familia: filtros.codigoFamilia || null,
    segmentos: filtros.segmentos,
    estagios: filtros.estagios,
    page,
    page_size: PAGE_SIZE,
    ordenar_por: orden.ordenarPor,
    direcao: orden.direcao,
  });

  const buscar = async (filtros, page, orden) => {
    setCarregando(true);
    try {
      const dados = await api.listarFichaEmpreendimento(
        montarRequest(filtros, page, orden)
      );
      setResultado(dados);
    } catch (err) {
      const mensagem = err && err.message ? err.message : 'Erro ao gerar relatório.';
      toast.error(mensagem);
      setResultado(null);
    } finally {
      setCarregando(false);
    }
  };

  const handleGerar = (filtros) => {
    setFiltrosAplicados(filtros);
    setPaginaAtual(1);
    setOrdenacao(ORDENACAO_INICIAL);
    buscar(filtros, 1, ORDENACAO_INICIAL);
  };

  const handleLimpar = () => {
    setFiltrosAplicados(null);
    setResultado(null);
    setPaginaAtual(1);
    setOrdenacao(ORDENACAO_INICIAL);
  };

  const handleMudarPagina = (novaPagina) => {
    setPaginaAtual(novaPagina);
    if (filtrosAplicados) {
      buscar(filtrosAplicados, novaPagina, ordenacao);
    }
  };

  const handleMudarOrdenacao = (novaOrdenacao) => {
    setOrdenacao(novaOrdenacao);
    setPaginaAtual(1);
    if (filtrosAplicados) {
      buscar(filtrosAplicados, 1, novaOrdenacao);
    }
  };

  const handleExportarExcel = async () => {
    if (!filtrosAplicados) return;
    setExportando(true);
    try {
      const blob = await api.exportarFichaEmpreendimentoExcel(
        montarRequest(filtrosAplicados, 1, ordenacao)
      );
      const url = window.URL.createObjectURL(
        new Blob([blob], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        })
      );
      const timestamp = moment().format('YYYY-MM-DD_HHmmss');
      const link = document.createElement('a');
      link.href = url;
      link.download = `ficha-empreendimento_${timestamp}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      const mensagem =
        err && err.message ? err.message : 'Erro ao exportar para Excel.';
      toast.error(mensagem);
    } finally {
      setExportando(false);
    }
  };

  // Edge case (LICOES §1.4): deletar/mudar filtro deve resetar paginação.
  // Aqui não há delete; mas se total mudou e estamos numa página inválida, voltar.
  useEffect(() => {
    if (
      resultado &&
      resultado.total > 0 &&
      paginaAtual > Math.ceil(resultado.total / PAGE_SIZE)
    ) {
      handleMudarPagina(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resultado]);

  const totalRegistros = resultado ? resultado.total : 0;
  const empreendimentos = resultado ? resultado.empreendimentos : [];

  return (
    <Container fluid className="px-3 py-3">
      <HeaderAzul title="Ficha de Empreendimento" />

      <Row>
        <Col xs={12} lg={4} xl={3} className="mb-3">
          <PainelFiltros
            valoresIniciais={ESTADO_INICIAL_FILTROS}
            onGerar={handleGerar}
            onLimpar={handleLimpar}
            carregando={carregando}
          />
        </Col>

        <Col xs={12} lg={8} xl={9}>
          {filtrosAplicados && (
            <Card className="mb-3 shadow-sm">
              <Card.Body className="py-2">
                <div className="d-flex justify-content-between align-items-center flex-wrap">
                  <ChipsFiltros filtros={filtrosAplicados} />
                  <Button
                    size="sm"
                    variant="success"
                    onClick={handleExportarExcel}
                    disabled={
                      exportando || carregando || totalRegistros === 0
                    }
                  >
                    {exportando ? (
                      <Spinner
                        animation="border"
                        size="sm"
                        className="mr-1"
                      />
                    ) : (
                      <FaFileExcel className="mr-1" />
                    )}
                    Exportar Excel
                  </Button>
                </div>
              </Card.Body>
            </Card>
          )}

          {carregando && (
            <div className="text-center py-4">
              <Spinner animation="border" />
              <div className="mt-2 text-muted">Carregando relatório…</div>
            </div>
          )}

          {!carregando && filtrosAplicados && resultado && (
            <TabelaPrincipal
              empreendimentos={empreendimentos}
              totalRegistros={totalRegistros}
              paginaAtual={paginaAtual}
              pageSize={PAGE_SIZE}
              ordenacao={ordenacao}
              onMudarPagina={handleMudarPagina}
              onMudarOrdenacao={handleMudarOrdenacao}
            />
          )}

          {!carregando && !filtrosAplicados && (
            <Card className="text-center py-5 shadow-sm">
              <Card.Body>
                <p className="text-muted mb-0">
                  Configure os filtros e clique em <strong>Gerar Relatório</strong>{' '}
                  para visualizar a Ficha de Empreendimento.
                </p>
              </Card.Body>
            </Card>
          )}
        </Col>
      </Row>
    </Container>
  );
};

export default FichaEmpreendimento;
